import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaHome,
  FaUsers,
  FaCheck,
  FaArrowRight,
  FaTimes,
  FaHeart,
} from "react-icons/fa";
import {
  Zap,
  ShieldCheck,
  Clock,
  Smartphone,
  Headset,
  TrendingUp,
  Building2,
  CheckCircle2,
  XCircle,
  Users,
  Star,
  ChevronDown,
  ChevronUp,
  Receipt,
  Sparkles,
  Send,
  Wallet,
  Award,
  HelpCircle,
  QrCode,
  ArrowRight,
  Bell,
  Camera,
  Check,
  PhoneCall,
  Copy,
  ExternalLink,
  X,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "../styles/user/SPHomeLanding.css";
import "../styles/user/SeoContent.css";
import staticContent from "../seo/StaticContent.mjs";

// Import local image assets bundled by Webpack
import heroBannerImg from "../assets/sphome/hero-banner.png";
import landlordWomanImg from "../assets/sphome/landlord-woman.jpg";
import ctvManImg from "../assets/sphome/ctv-man.jpg";
import modernVillasImg from "../assets/sphome/modern-villas.jpg";

const { ProductOverview } = staticContent;

export default function SPHomeLanding() {
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState(null); // 'landlord' | 'ctv' | 'start' | null
  const [howItWorksTab, setHowItWorksTab] = useState("landlord"); // 'landlord' | 'ctv'
  const [openFaq, setOpenFaq] = useState(0); // active FAQ index

  const [showZaloCard, setShowZaloCard] = useState(false);
  const [copiedZalo, setCopiedZalo] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Close Zalo card on outside click
  useEffect(() => {
    if (!showZaloCard) return;
    const handleOutsideClick = (e) => {
      if (
        !e.target.closest(".sp-zalo-popover-card") &&
        !e.target.closest(".sp-float-btn.zalo")
      ) {
        setShowZaloCard(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showZaloCard]);

  const handleZaloButtonClick = (e) => {
    e.preventDefault();
    const isMobile =
      /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
      window.innerWidth <= 768;
    if (isMobile) {
      window.open("https://zalo.me/0911742286", "_blank");
      return;
    }
    setShowZaloCard((prev) => !prev);
  };

  const handleCopyZaloPhone = () => {
    navigator.clipboard.writeText("0911742286");
    setCopiedZalo(true);
    setTimeout(() => setCopiedZalo(false), 2200);
  };

  // Animations
  const fadeIn = {
    hidden: { opacity: 0, y: 25 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const cardVariant = (delay = 0) => ({
    hidden: { opacity: 0, y: 35 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay, ease: "easeOut" } },
  });

  return (
    <div className="sp-landing-wrapper">
      {/* Optional Integrated Header */}
      <Header />

      {/* ====================================================================
          1. HERO SECTION & BANNER (Senior PropTech SaaS Design)
          ==================================================================== */}
      <section className="sp-hero-wrapper">
        <div className="sp-hero-banner-box">
          <motion.div
            className="sp-hero-content"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            {/* Top Announcement Badge: 100% Native Font, Zero Accents Glitch */}
            <div className="sp-hero-badge-pill">
              <span className="sp-hero-badge-dot" />
              <Sparkles size={14} className="sp-hero-badge-icon" />
              <span>Giải pháp quản lý nhà trọ &amp; kết nối khách thuê</span>
            </div>

            {/* SP Home Logo Brand */}
            <div className="sp-brand-header">
              <div className="sp-brand-logo-text">
                <span className="sp-logo-green">SP</span>{" "}
                <span className="sp-logo-navy">Home</span>
              </div>
            </div>

            {/* Main Headlines */}
            <div className="sp-main-headline">
              <h1 className="sp-headline-navy">Phần mềm quản lý nhà trọ thông minh</h1>
              <h2 className="sp-headline-green">Kết nối CTV – Lấp đầy phòng trống</h2>
            </div>

            {/* Subtitle */}
            <p className="sp-hero-subtitle">
              <span>Quản lý phòng trọ, khách thuê, hợp đồng, hóa đơn điện nước và công nợ;</span>
              <span>kết nối cộng tác viên để tìm khách thuê phòng.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* ====================================================================
          2. TWO DUAL SOLUTIONS CARDS (SPLIT SECTION)
          ==================================================================== */}
      {/* ====================================================================
          2. TWO DUAL VALUE CARDS (Inspired by Live AI Style - Ảnh 2)
          ==================================================================== */}
      <section className="sp-cards-container">
        {/* ==================== CARD 1: DÀNH CHO CHỦ NHÀ / QUẢN LÝ (Cyan / Emerald Theme) ==================== */}
        <motion.div
          className="sp-pro-card sp-pro-card-landlord"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={cardVariant(0.1)}
        >
          {/* Top Meta Bar */}
          <div className="sp-card-meta-bar">
            <div className="sp-meta-pill landlord">
              <Zap size={13} />
              <span>TIẾT KIỆM 90% THỜI GIAN</span>
            </div>
          </div>

          {/* Card Hero Header (Squircle + Title + Subtitle) */}
          <div className="sp-card-hero-header">
            <div className="sp-hero-squircle landlord">
              <Building2 size={28} />
            </div>
            <div className="sp-hero-header-text">
              <h3 className="sp-hero-title landlord">CHỦ NHÀ &amp; QUẢN LÝ</h3>
              <p className="sp-hero-tagline">Tự động hóa 100% – Vận hành thảnh thơi</p>
            </div>
          </div>

          {/* Description */}
          <p className="sp-card-lead-desc">
            Giải pháp tối ưu cho chủ nhà, xưởng phòng trọ và chuỗi căn hộ dịch vụ. Tự động chốt điện nước, xuất hóa đơn Zalo trong 30 giây và kiểm soát công nợ tức thì.
          </p>

          {/* Split Content: Left Checklist + Right Interactive Showcase Widget */}
          <div className="sp-card-inner-split">
            {/* Left Column: Feature Checklist */}
            <ul className="sp-ai-feature-list">
              <li className="sp-ai-feature-item">
                <CheckCircle2 size={18} className="check-icon landlord" />
                <span>Quản lý chuỗi dãy phòng, nhiều cơ sở tập trung</span>
              </li>
              <li className="sp-ai-feature-item">
                <CheckCircle2 size={18} className="check-icon landlord" />
                <span>Chốt điện nước, tự xuất hóa đơn Zalo trong 30s</span>
              </li>
              <li className="sp-ai-feature-item">
                <CheckCircle2 size={18} className="check-icon landlord" />
                <span>Kiểm soát công nợ, dòng tiền &amp; tiền cọc minh bạch</span>
              </li>
              <li className="sp-ai-feature-item">
                <CheckCircle2 size={18} className="check-icon landlord" />
                <span>Số hóa hợp đồng thuê, nhắc hạn trước 30 ngày</span>
              </li>
              <li className="sp-ai-feature-item">
                <CheckCircle2 size={18} className="check-icon landlord" />
                <span>Phân quyền chi tiết cho quản lý, kế toán, kỹ thuật</span>
              </li>
            </ul>

            {/* Right Column: High-Tech Showcase Widget (styled like the Mic Widget in Ảnh 2) */}
            <div className="sp-ai-showcase-widget landlord-widget">
              <div className="widget-glow-circle landlord" />

              {/* Top Pill inside widget */}
              <div className="widget-top-pill landlord">
                <Zap size={13} />
                <span>Hóa đơn Zalo trong 30s</span>
              </div>

              {/* Center Dashboard Showcase with animated wave bars */}
              <div className="widget-center-content">
                <div className="side-soundwave left landlord">
                  <span />
                  <span />
                  <span />
                </div>

                <div className="widget-preview-card landlord">
                  <div className="preview-card-head">
                    <div className="status-indicator">
                      <span className="status-dot green" />
                      <span className="building-name">SP Home Riverside • 24 Phòng</span>
                    </div>
                    <span className="badge-online">Trực tuyến</span>
                  </div>

                  <div className="preview-mini-stats">
                    <div className="mini-stat-col">
                      <span className="label">Đã thuê</span>
                      <span className="val green">22/24 (92%)</span>
                    </div>
                    <div className="mini-stat-col">
                      <span className="label">Doanh thu</span>
                      <span className="val">68.500.000 ₫</span>
                    </div>
                    <div className="mini-stat-col">
                      <span className="label">Công nợ tồn</span>
                      <span className="val green">0 ₫</span>
                    </div>
                  </div>

                  {/* Sample Zalo invoice snippet */}
                  <div className="preview-invoice-snippet">
                    <div className="snippet-head">
                      <span className="room-id">Phòng 202</span>
                      <span className="tenant">Nguyễn Văn An</span>
                      <span className="zalo-tag">✓ Đã gửi Zalo</span>
                    </div>
                    <div className="snippet-body">
                      <span>⚡ 185 kWh • 💧 6 m³</span>
                      <span className="snippet-total">3.450.000 ₫</span>
                    </div>
                  </div>
                </div>

                <div className="side-soundwave right landlord">
                  <span />
                  <span />
                  <span />
                </div>
              </div>

              {/* Bottom Toast inside widget */}
              <div className="widget-bottom-toast landlord">
                <Check size={13} />
                <span>Tự động chốt số &amp; gửi Zalo 24/7</span>
              </div>
            </div>
          </div>

          {/* Card Bottom CTA */}
          <div className="sp-card-bottom-bar">
            <button
              className="sp-pro-action-btn landlord-btn"
              onClick={() => navigate("/admin/dashboard")}
            >
              <Building2 size={18} />
              <span>Quản lý nhà trọ ngay</span>
              <ArrowRight size={16} />
            </button>
            <span className="sp-card-subtext">
              Dùng thử miễn phí • Không cần cài đặt phức tạp
            </span>
          </div>
        </motion.div>

        {/* ==================== CARD 2: DÀNH CHO CỘNG TÁC VIÊN (CTV) (Purple / Tech Blue Theme) ==================== */}
        <motion.div
          className="sp-pro-card sp-pro-card-ctv"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={cardVariant(0.25)}
        >
          {/* Top Meta Bar */}
          <div className="sp-card-meta-bar">
            <div className="sp-meta-pill ctv-live">
              <span className="live-dot" />
              <span>TRỰC TUYẾN</span>
            </div>
            <div className="sp-meta-pill ctv-highlight">
              <Sparkles size={13} />
              <span>100% HOA HỒNG MINH BẠCH</span>
            </div>
          </div>

          {/* Card Hero Header (Squircle + Title + Subtitle) */}
          <div className="sp-card-hero-header">
            <div className="sp-hero-squircle ctv">
              <Users size={28} />
            </div>
            <div className="sp-hero-header-text">
              <h3 className="sp-hero-title ctv">CỘNG TÁC VIÊN (CTV)</h3>
              <p className="sp-hero-tagline">Kho phòng chính chủ – Hoa hồng nhận ngay</p>
            </div>
          </div>

          {/* Description */}
          <p className="sp-card-lead-desc">
            Tiếp cận kho phòng trống dồi dào trực tiếp từ hàng nghìn chủ nhà uy tín. Thông tin giá cả, hoa hồng minh bạch, có quản lý hỗ trợ mở cửa dẫn khách 24/7.
          </p>

          {/* Split Content: Left Checklist + Right Smartphone Mockup */}
          <div className="sp-card-inner-split">
            {/* Left Column: Feature Checklist */}
            <ul className="sp-ai-feature-list">
              <li className="sp-ai-feature-item">
                <CheckCircle2 size={18} className="check-icon ctv" />
                <span>Kho giỏ hàng phòng trống cập nhật real-time</span>
              </li>
              <li className="sp-ai-feature-item">
                <CheckCircle2 size={18} className="check-icon ctv" />
                <span>Hoa hồng công khai từng căn (500k - 2 triệu/phòng)</span>
              </li>
              <li className="sp-ai-feature-item">
                <CheckCircle2 size={18} className="check-icon ctv" />
                <span>Tải ảnh đẹp, video nét và bài đăng mẫu chỉ với 1 chạm</span>
              </li>
              <li className="sp-ai-feature-item">
                <CheckCircle2 size={18} className="check-icon ctv" />
                <span>Hỗ trợ mở cửa, dẫn khách xem phòng linh hoạt 24/7</span>
              </li>
              <li className="sp-ai-feature-item">
                <CheckCircle2 size={18} className="check-icon ctv" />
                <span>Khớp lệnh cọc và nhận hoa hồng nhanh về tài khoản</span>
              </li>
            </ul>

            {/* Right Column: High-Tech Phone Mockup (styled like the Smartphone in Ảnh 2) */}
            <div className="sp-ai-showcase-widget ctv-widget">
              <div className="widget-glow-circle ctv" />

              {/* Floating Pill at top */}
              <div className="widget-top-pill ctv">
                <Sparkles size={13} />
                <span>12 Phòng mới hôm nay</span>
              </div>

              {/* Smartphone Frame */}
              <div className="sp-smartphone-mockup">
                <div className="smartphone-camera-notch" />
                <div className="smartphone-screen">
                  {/* Live Stream Bar */}
                  <div className="stream-header-bar">
                    <div className="stream-live-tag">
                      <span className="stream-dot" />
                      <span>LIVE</span>
                    </div>
                    <div className="stream-viewers">👥 1.2k CTV online</div>
                  </div>

                  {/* Room Card Preview */}
                  <div className="stream-room-card">
                    <img
                      src={modernVillasImg}
                      alt="Hình ảnh phòng trọ mẫu trên phần mềm quản lý nhà trọ SP Home"
                      className="stream-room-img"
                    />
                    <div className="stream-room-info">
                      <div className="room-title">Studio Ban công thoáng</div>
                      <div className="room-loc">📍 Hai Bà Trưng • Full đồ</div>
                      <div className="room-price-commission">
                        <span className="price">3.800.000 ₫/th</span>
                        <span className="commission-tag">Hoa hồng 1.000.000 ₫ 💎</span>
                      </div>
                    </div>
                  </div>

                  {/* Floating Comment / Toast like in Ảnh 2 */}
                  <div className="stream-chat-toast">
                    <span className="chat-avatar">🎉</span>
                    <div className="chat-msg">
                      <strong>CTV Hoàng Nam</strong> vừa chốt Phòng 302 • Nhận 1.000.000 ₫ ✓
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card Bottom CTA */}
          <div className="sp-card-bottom-bar">
            <button
              className="sp-pro-action-btn ctv-btn"
              onClick={() => navigate("/rooms")}
            >
              <Users size={18} />
              <span>Xem giỏ phòng nhận hoa hồng</span>
              <ArrowRight size={16} />
            </button>
            <span className="sp-card-subtext">
              Không ôm cọc • Rút hoa hồng về tài khoản trong ngày
            </span>
          </div>
        </motion.div>
      </section>

      {/* ====================================================================
          2.5. QUY TRÌNH VẬN HÀNH 3 BƯỚC ĐƠN GIẢN (HOW IT WORKS - INTERACTIVE)
          ==================================================================== */}
      <section className="sp-how-it-works-section">
        <div className="sp-section-container">
          <motion.div
            className="sp-section-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <div className="sp-section-pill">
              <Sparkles size={15} />
              <span>Dễ dàng bắt đầu</span>
            </div>
            <h2 className="sp-section-title">Quy trình vận hành 3 bước đơn giản</h2>
            <p className="sp-section-subtitle">
              Tối ưu hóa thời gian và quy trình với hệ thống khép kín, minh bạch và tự động hóa cao.
            </p>

            {/* Interactive Tabs Switcher */}
            <div className="sp-tab-switch-box">
              <button
                className={`sp-tab-btn ${howItWorksTab === "landlord" ? "active" : ""}`}
                onClick={() => setHowItWorksTab("landlord")}
              >
                <Building2 size={18} />
                <span>Dành cho Chủ nhà &amp; Quản lý</span>
              </button>
              <button
                className={`sp-tab-btn ${howItWorksTab === "ctv" ? "active" : ""}`}
                onClick={() => setHowItWorksTab("ctv")}
              >
                <Users size={18} />
                <span>Dành cho Cộng tác viên (CTV)</span>
              </button>
            </div>
          </motion.div>

          {/* Tab Content Cards */}
          <div className="sp-steps-grid">
            {howItWorksTab === "landlord" ? (
              <>
                <motion.div
                  className="sp-step-card"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariant(0.08)}
                >
                  <div className="sp-step-header">
                    <span className="sp-step-number-tag">Bước 01</span>
                    <div className="sp-step-icon-wrap green">
                      <Building2 size={26} />
                    </div>
                  </div>
                  <h3 className="sp-step-title">Thiết lập tòa nhà &amp; phòng</h3>
                  <p className="sp-step-desc">
                    Tạo danh sách phòng, định mức giá thuê, đơn giá điện nước chỉ trong 2 phút. Hệ thống sẵn sàng vận hành tức thì.
                  </p>
                  <div className="sp-step-bullets">
                    <div className="sp-step-bullet-item">
                      <CheckCircle2 size={16} className="bullet-icon green" />
                      <span>Sơ đồ phòng trực quan, linh hoạt</span>
                    </div>
                    <div className="sp-step-bullet-item">
                      <CheckCircle2 size={16} className="bullet-icon green" />
                      <span>Mẫu hợp đồng pháp lý chuẩn mực</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="sp-step-card highlighted"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariant(0.16)}
                >
                  <div className="sp-step-header">
                    <span className="sp-step-number-tag featured">Bước 02</span>
                    <div className="sp-step-icon-wrap blue">
                      <Users size={26} />
                    </div>
                  </div>
                  <h3 className="sp-step-title">Kết nối 4.200+ CTV bán phòng</h3>
                  <p className="sp-step-desc">
                    Phòng trống được tự động đồng bộ lên sàn. Mạng lưới CTV chủ động tìm khách, đặt lịch hẹn dẫn xem và chốt cọc nhanh chóng.
                  </p>
                  <div className="sp-step-bullets">
                    <div className="sp-step-bullet-item">
                      <CheckCircle2 size={16} className="bullet-icon blue" />
                      <span>Lấp đầy phòng chỉ trong 7 - 10 ngày</span>
                    </div>
                    <div className="sp-step-bullet-item">
                      <CheckCircle2 size={16} className="bullet-icon blue" />
                      <span>Chủ động phê duyệt lịch hẹn dẫn khách</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="sp-step-card"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariant(0.24)}
                >
                  <div className="sp-step-header">
                    <span className="sp-step-number-tag">Bước 03</span>
                    <div className="sp-step-icon-wrap purple">
                      <Receipt size={26} />
                    </div>
                  </div>
                  <h3 className="sp-step-title">Tự động thu tiền &amp; Báo cáo</h3>
                  <p className="sp-step-desc">
                    Nhập chỉ số điện nước 1 chạm, tự động tạo và gửi hóa đơn Zalo/SMS tới khách thuê. Báo cáo dòng tiền thu chi chính xác.
                  </p>
                  <div className="sp-step-bullets">
                    <div className="sp-step-bullet-item">
                      <CheckCircle2 size={16} className="bullet-icon purple" />
                      <span>Nhắc nợ tự động, lịch sự</span>
                    </div>
                    <div className="sp-step-bullet-item">
                      <CheckCircle2 size={16} className="bullet-icon purple" />
                      <span>Báo cáo doanh thu thời gian thực</span>
                    </div>
                  </div>
                </motion.div>
              </>
            ) : (
              <>
                <motion.div
                  className="sp-step-card"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariant(0.08)}
                >
                  <div className="sp-step-header">
                    <span className="sp-step-number-tag">Bước 01</span>
                    <div className="sp-step-icon-wrap cyan">
                      <Sparkles size={26} />
                    </div>
                  </div>
                  <h3 className="sp-step-title">Đăng ký &amp; Xem giỏ hàng thật</h3>
                  <p className="sp-step-desc">
                    Tạo tài khoản CTV miễn phí trong 1 phút. Tiếp cận kho hàng nghìn phòng trống 100% đã xác thực giá niêm yết và hình ảnh thực tế.
                  </p>
                  <div className="sp-step-bullets">
                    <div className="sp-step-bullet-item">
                      <CheckCircle2 size={16} className="bullet-icon cyan" />
                      <span>Không cần đóng cọc hay vốn đầu tư</span>
                    </div>
                    <div className="sp-step-bullet-item">
                      <CheckCircle2 size={16} className="bullet-icon cyan" />
                      <span>Cập nhật tình trạng phòng theo giây</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="sp-step-card highlighted"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariant(0.16)}
                >
                  <div className="sp-step-header">
                    <span className="sp-step-number-tag featured">Bước 02</span>
                    <div className="sp-step-icon-wrap orange">
                      <Send size={26} />
                    </div>
                  </div>
                  <h3 className="sp-step-title">Đăng tin &amp; Dẫn khách xem phòng</h3>
                  <p className="sp-step-desc">
                    Tải nhanh bộ ảnh và nội dung chuẩn SEO để đăng lên các hội nhóm mạng xã hội. Đặt lịch hẹn dẫn khách xem phòng trực tiếp trên app.
                  </p>
                  <div className="sp-step-bullets">
                    <div className="sp-step-bullet-item">
                      <CheckCircle2 size={16} className="bullet-icon orange" />
                      <span>Công cụ soạn tin đăng nhanh 1 chạm</span>
                    </div>
                    <div className="sp-step-bullet-item">
                      <CheckCircle2 size={16} className="bullet-icon orange" />
                      <span>Chủ nhà phối hợp hỗ trợ mở cửa chu đáo</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="sp-step-card"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariant(0.24)}
                >
                  <div className="sp-step-header">
                    <span className="sp-step-number-tag">Bước 03</span>
                    <div className="sp-step-icon-wrap emerald">
                      <Wallet size={26} />
                    </div>
                  </div>
                  <h3 className="sp-step-title">Chốt cọc &amp; Nhận hoa hồng ngay</h3>
                  <p className="sp-step-desc">
                    Khách cọc và ký hợp đồng thành công, hoa hồng được ghi nhận tự động vào ví tài khoản và chuyển khoản ngân hàng chuẩn hẹn.
                  </p>
                  <div className="sp-step-bullets">
                    <div className="sp-step-bullet-item">
                      <CheckCircle2 size={16} className="bullet-icon emerald" />
                      <span>Hoa hồng cao, rõ ràng trên từng phòng</span>
                    </div>
                    <div className="sp-step-bullet-item">
                      <CheckCircle2 size={16} className="bullet-icon emerald" />
                      <span>Đối soát minh bạch, rút tiền tức thì</span>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ====================================================================
          3. "VÌ SAO NÊN CHỌN SP HOME?" SECTION
          ==================================================================== */}
      <section className="sp-why-section">
        <motion.div
          className="sp-why-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <h2 className="sp-why-title">Vì sao nên chọn SP Home?</h2>
          <div className="sp-why-title-accent" />
        </motion.div>

        {/* 4 Cards in Top Grid */}
        <div className="sp-why-grid">
          {/* 1. Đơn giản */}
          <motion.div
            className="sp-why-card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={cardVariant(0.05)}
          >
            <div className="sp-icon-bubble sp-bubble-green">
              <Zap size={30} strokeWidth={2.2} />
            </div>
            <h3 className="sp-why-item-title">Đơn giản</h3>
            <p className="sp-why-item-desc">
              Giao diện thân thiện,
              {"\n"}dễ sử dụng
            </p>
          </motion.div>

          {/* 2. An toàn */}
          <motion.div
            className="sp-why-card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={cardVariant(0.12)}
          >
            <div className="sp-icon-bubble sp-bubble-blue">
              <ShieldCheck size={30} strokeWidth={2.2} />
            </div>
            <h3 className="sp-why-item-title">An toàn</h3>
            <p className="sp-why-item-desc">
              Dữ liệu bảo mật,
              {"\n"}phân quyền rõ ràng
            </p>
          </motion.div>

          {/* 3. Hiệu quả */}
          <motion.div
            className="sp-why-card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={cardVariant(0.19)}
          >
            <div className="sp-icon-bubble sp-bubble-purple">
              <Clock size={30} strokeWidth={2.2} />
            </div>
            <h3 className="sp-why-item-title">Hiệu quả</h3>
            <p className="sp-why-item-desc">
              Tiết kiệm thời gian,
              {"\n"}tối ưu chi phí
            </p>
          </motion.div>

          {/* 4. Đa nền tảng */}
          <motion.div
            className="sp-why-card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={cardVariant(0.26)}
          >
            <div className="sp-icon-bubble sp-bubble-cyan">
              <Smartphone size={30} strokeWidth={2.2} />
            </div>
            <h3 className="sp-why-item-title">Đa nền tảng</h3>
            <p className="sp-why-item-desc">
              Sử dụng trên web,
              {"\nmobile mọi lúc mọi nơi"}
            </p>
          </motion.div>
        </div>

        {/* Bottom Row: 2 Cards + Handwritten companion & Villa scene */}
        <div className="sp-why-bottom-row">
          {/* 5. Hỗ trợ tận tâm */}
          <motion.div
            className="sp-why-card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={cardVariant(0.1)}
          >
            <div className="sp-icon-bubble sp-bubble-orange">
              <Headset size={30} strokeWidth={2.2} />
            </div>
            <h3 className="sp-why-item-title">Hỗ trợ tận tâm</h3>
            <p className="sp-why-item-desc">Đội ngũ hỗ trợ 24/7</p>
          </motion.div>

          {/* 6. Phát triển lâu dài */}
          <motion.div
            className="sp-why-card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={cardVariant(0.18)}
          >
            <div className="sp-icon-bubble sp-bubble-emerald">
              <TrendingUp size={30} strokeWidth={2.2} />
            </div>
            <h3 className="sp-why-item-title">Phát triển lâu dài</h3>
            <p className="sp-why-item-desc">
              Luôn lắng nghe
              {"\nvà cải tiến"}
            </p>
          </motion.div>

          {/* Showcase card: Handwritten Note & Modern Villa Architecture */}
          <motion.div
            className="sp-villa-showcase"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={cardVariant(0.25)}
          >
            <div className="sp-companion-note">
              <span className="sp-companion-brand">SP Home</span>
              <span className="sp-companion-text">Đồng hành cùng bạn</span>
              <span className="sp-companion-text">trên hành trình kinh doanh!</span>
            </div>
            <div className="sp-villa-img-box">
              <img
                src={modernVillasImg}
                alt="Biệt thự hiện đại SP Home"
                className="sp-villa-img"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ====================================================================
          4. IMPACT METRICS & SOCIAL PROOF
          ==================================================================== */}
      <section className="sp-metrics-section">
        <div className="sp-section-container">
          <motion.div
            className="sp-section-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <div className="sp-section-pill">
              <Award size={15} />
              <span>Thành tựu ấn tượng</span>
            </div>
            <h2 className="sp-section-title">Những con số khẳng định uy tín</h2>
            <p className="sp-section-subtitle">
              Hơn 1.500 chủ nhà và 4.200 CTV đã tin tưởng đồng hành cùng SP Home trên hành trình số hóa quản lý phòng trọ.
            </p>
          </motion.div>

          <div className="sp-metrics-grid">
            <motion.div
              className="sp-metric-card"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariant(0.08)}
            >
              <div className="sp-metric-icon-wrap green">
                <Building2 size={28} />
              </div>
              <div className="sp-metric-value">1.500+</div>
              <div className="sp-metric-label">Tòa nhà &amp; Dãy trọ</div>
              <div className="sp-metric-desc">Đang vận hành mượt mà mỗi ngày</div>
            </motion.div>

            <motion.div
              className="sp-metric-card"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariant(0.16)}
            >
              <div className="sp-metric-icon-wrap blue">
                <Users size={28} />
              </div>
              <div className="sp-metric-value">4.200+</div>
              <div className="sp-metric-label">Cộng tác viên (CTV)</div>
              <div className="sp-metric-desc">Mạng lưới môi giới năng động toàn quốc</div>
            </motion.div>

            <motion.div
              className="sp-metric-card"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariant(0.24)}
            >
              <div className="sp-metric-icon-wrap orange">
                <Zap size={28} />
              </div>
              <div className="sp-metric-value">98.2%</div>
              <div className="sp-metric-label">Tỷ lệ lấp đầy phòng</div>
              <div className="sp-metric-desc">Trung bình có khách cốt trong 7 - 10 ngày</div>
            </motion.div>

            <motion.div
              className="sp-metric-card"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariant(0.32)}
            >
              <div className="sp-metric-icon-wrap purple">
                <ShieldCheck size={28} />
              </div>
              <div className="sp-metric-value">25+ Tỷ</div>
              <div className="sp-metric-label">Doanh thu đối soát / tháng</div>
              <div className="sp-metric-desc">Chính xác tuyệt đối, không thất thoát</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ====================================================================
          5. BẢNG SO SÁNH: TRUYỀN THỐNG VS SP HOME
          ==================================================================== */}
      <section className="sp-comparison-section">
        <div className="sp-section-container">
          <motion.div
            className="sp-section-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <div className="sp-section-pill">
              <Sparkles size={15} />
              <span>Sự khác biệt vượt trội</span>
            </div>
            <h2 className="sp-section-title">Cách truyền thống vs SP Home</h2>
            <p className="sp-section-subtitle">
              Xem SP Home giải phóng bạn khỏi áp lực ghi chép sổ sách và nỗi lo phòng trống như thế nào.
            </p>
          </motion.div>

          <div className="sp-comparison-table">
            <div className="sp-comparison-header-row">
              <div className="sp-comp-col label-col">Hạng mục so sánh</div>
              <div className="sp-comp-col old-col">Quản lý truyền thống</div>
              <div className="sp-comp-col new-col">
                <span className="sp-new-badge">Khuyên dùng</span>
                <span>Giải pháp SP Home</span>
              </div>
            </div>

            <div className="sp-comparison-row">
              <div className="sp-comp-col label-col">
                <strong>Sổ sách &amp; Dữ liệu</strong>
              </div>
              <div className="sp-comp-col old-col">
                <div className="sp-status-bad">
                  <XCircle size={18} />
                  <span>Sổ tay, Excel rời rạc dễ mất mát dữ liệu, tốn 2-3 tiếng mỗi lần tính toán</span>
                </div>
              </div>
              <div className="sp-comp-col new-col">
                <div className="sp-status-good">
                  <CheckCircle2 size={18} />
                  <span>Đồng bộ đám mây trên điện thoại &amp; máy tính, sao lưu 24/7 an toàn</span>
                </div>
              </div>
            </div>

            <div className="sp-comparison-row">
              <div className="sp-comp-col label-col">
                <strong>Lấp đầy phòng trống</strong>
              </div>
              <div className="sp-comp-col old-col">
                <div className="sp-status-bad">
                  <XCircle size={18} />
                  <span>Tự đăng tin lẻ loi, phòng trống 1-2 tháng mất cả chục triệu tiền chờ</span>
                </div>
              </div>
              <div className="sp-comp-col new-col">
                <div className="sp-status-good">
                  <CheckCircle2 size={18} />
                  <span>Hệ sinh thái 4.200+ CTV tiếp cận ngay lập tức, chốt phòng thần tốc</span>
                </div>
              </div>
            </div>

            <div className="sp-comparison-row">
              <div className="sp-comp-col label-col">
                <strong>Tính điện nước &amp; Thu tiền</strong>
              </div>
              <div className="sp-comp-col old-col">
                <div className="sp-status-bad">
                  <XCircle size={18} />
                  <span>Cuối tháng đi chép từng công tơ, cộng tay nhầm lẫn, ngại ngùng đòi nợ</span>
                </div>
              </div>
              <div className="sp-comp-col new-col">
                <div className="sp-status-good">
                  <CheckCircle2 size={18} />
                  <span>Nhập số 1 chạm, tự xuất hóa đơn Zalo/SMS, nhắc nợ lịch sự tự động</span>
                </div>
              </div>
            </div>

            <div className="sp-comparison-row">
              <div className="sp-comp-col label-col">
                <strong>Minh bạch hoa hồng CTV</strong>
              </div>
              <div className="sp-comp-col old-col">
                <div className="sp-status-bad">
                  <XCircle size={18} />
                  <span>Môi giới lo bị cắt cầu, chủ nhà không rõ khách do CTV nào dẫn</span>
                </div>
              </div>
              <div className="sp-comp-col new-col">
                <div className="sp-status-good">
                  <CheckCircle2 size={18} />
                  <span>Theo dõi lịch hẹn minh bạch, hợp đồng chuẩn mực, chi trả đúng hẹn</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====================================================================
          6. MOBILE APP SHOWCASE
          ==================================================================== */}
      <section className="sp-mobile-showcase-section">
        <div className="sp-section-container">
          <div className="sp-mobile-banner-box">
            <div className="sp-mobile-left">
              <div className="sp-mobile-tag">
                <Smartphone size={15} />
                <span>Trải nghiệm di động tối ưu</span>
              </div>
              <h2 className="sp-mobile-title">
                Quản lý nhà trọ ngay trong túi áo của bạn
              </h2>
              <p className="sp-mobile-desc">
                Nhận thông báo biến động số dư, duyệt lịch xem phòng của CTV và gửi thông báo cho khách thuê tức thì trên ứng dụng iOS &amp; Android.
              </p>

              <div className="sp-mobile-features-list">
                <div className="sp-mfeature-item">
                  <div className="sp-mfeature-icon"><Bell size={18} /></div>
                  <div>
                    <h4>Thông báo thời gian thực</h4>
                    <p>Biết ngay khi khách chuyển khoản hoặc CTV tạo lịch hẹn dẫn khách</p>
                  </div>
                </div>
                <div className="sp-mfeature-item">
                  <div className="sp-mfeature-icon"><Camera size={18} /></div>
                  <div>
                    <h4>Ghi chỉ số điện nước bằng camera</h4>
                    <p>Nhận diện số tự động, không lo chép nhầm hay thất thoát điện nước</p>
                  </div>
                </div>
                <div className="sp-mfeature-item">
                  <div className="sp-mfeature-icon"><TrendingUp size={18} /></div>
                  <div>
                    <h4>Báo cáo tức thì 24/7</h4>
                    <p>Xem biểu đồ doanh thu và tỷ lệ lấp phòng mọi lúc, mọi nơi</p>
                  </div>
                </div>
              </div>

              <div className="sp-mobile-cta-row">
                <button
                  className="sp-btn-action btn-green"
                  onClick={() => setActiveModal("start")}
                >
                  <span>Trải nghiệm trên App</span>
                  <ArrowRight size={16} />
                </button>
                <div className="sp-app-badge-box">
                  <span className="badge-pill">⚡ Hỗ trợ iOS &amp; Android</span>
                </div>
              </div>
            </div>

            <div className="sp-mobile-right">
              <div className="sp-app-card-preview">
                <div className="sp-app-screen-mock">
                  <div className="sp-app-mock-header">
                    <span className="app-dot red" />
                    <span className="app-dot yellow" />
                    <span className="app-dot green" />
                    <span className="app-title">SP Home Mobile</span>
                  </div>
                  <div className="sp-app-mock-body">
                    <div className="sp-app-mini-card stat">
                      <div className="mini-label">Doanh thu tháng này</div>
                      <div className="mini-val">48.500.000đ <span className="up">+12%</span></div>
                    </div>
                    <div className="sp-app-mini-grid">
                      <div className="mini-sub-box">
                        <span>Đã thu</span>
                        <strong>42.000.000đ</strong>
                      </div>
                      <div className="mini-sub-box alert">
                        <span>Chưa thu</span>
                        <strong>6.500.000đ</strong>
                      </div>
                    </div>
                    <div className="sp-app-mini-card alert-card">
                      <Bell size={14} />
                      <span>Có 2 phòng trống sắp có CTV dẫn khách!</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====================================================================
          7. KHÁCH HÀNG & ĐỐI TÁC NÓI GÌ (TESTIMONIALS)
          ==================================================================== */}
      <section className="sp-testimonials-section">
        <div className="sp-section-container">
          <motion.div
            className="sp-section-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <div className="sp-section-pill">
              <Star size={15} />
              <span>Đánh giá từ cộng đồng</span>
            </div>
            <h2 className="sp-section-title">Khách hàng &amp; Đối tác nói gì về SP Home?</h2>
            <p className="sp-section-subtitle">
              Sự hài lòng và gắn bó lâu dài của khách hàng chính là giá trị cốt lõi mà SP Home luôn hướng đến.
            </p>
          </motion.div>

          <div className="sp-testimonials-grid">
            <motion.div
              className="sp-testimonial-card"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariant(0.08)}
            >
              <div className="sp-stars-row">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="#f59e0b" color="#f59e0b" />
                ))}
              </div>
              <p className="sp-testimonial-quote">
                “Trước đây cuối tháng tôi mất cả tuần đi ghi số điện nước, cộng tiền phòng và nhắn tin từng người. Dùng SP Home chỉ mất 15 phút là hóa đơn gửi tới từng khách thuê qua Zalo. Nhẹ đầu hơn hẳn!”
              </p>
              <div className="sp-reviewer-info">
                <img
                  src={landlordWomanImg}
                  alt="Anh Nguyễn Hoàng Nam"
                  className="sp-reviewer-avatar"
                />
                <div>
                  <h4 className="sp-reviewer-name">Anh Nguyễn Hoàng Nam</h4>
                  <p className="sp-reviewer-role">Chủ chuỗi 5 dãy trọ (Cầu Giấy &amp; Nam Từ Liêm)</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="sp-testimonial-card highlighted"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariant(0.16)}
            >
              <div className="sp-stars-row">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="#f59e0b" color="#f59e0b" />
                ))}
              </div>
              <p className="sp-testimonial-quote">
                “Kho phòng của SP Home cực kỳ chuẩn, ảnh thật giá thật 100%. Mình dẫn khách ưng là chốt cọc luôn trên app, hoa hồng nhận đúng hẹn và minh bạch nhất trong các nền tảng mình từng làm.”
              </p>
              <div className="sp-reviewer-info">
                <img
                  src={ctvManImg}
                  alt="Anh Trần Minh Tuấn"
                  className="sp-reviewer-avatar"
                />
                <div>
                  <h4 className="sp-reviewer-name">Anh Trần Minh Tuấn</h4>
                  <p className="sp-reviewer-role">Cộng tác viên BĐS xuất sắc (Hà Nội)</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="sp-testimonial-card"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariant(0.24)}
            >
              <div className="sp-stars-row">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="#f59e0b" color="#f59e0b" />
                ))}
              </div>
              <p className="sp-testimonial-quote">
                “Tỷ lệ lấp đầy phòng của SP Home quá nhanh. Đợt sinh viên ra trường vừa rồi 8 phòng trống của bên mình được đội ngũ CTV chốt sạch chỉ trong 10 ngày. Cực kỳ ấn tượng!”
              </p>
              <div className="sp-reviewer-info">
                <img
                  src={landlordWomanImg}
                  alt="Chị Lê Thanh Hương"
                  className="sp-reviewer-avatar"
                />
                <div>
                  <h4 className="sp-reviewer-name">Chị Lê Thanh Hương</h4>
                  <p className="sp-reviewer-role">Quản lý căn hộ dịch vụ (Hà Đông, Hà Nội)</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ====================================================================
          8. CÂU HỎI THƯỜNG GẶP (FAQ ACCORDION)
          ==================================================================== */}
      <ProductOverview />
      <section className="sp-faq-section">
        <div className="sp-section-container">
          <motion.div
            className="sp-section-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <div className="sp-section-pill">
              <HelpCircle size={15} />
              <span>Hỗ trợ &amp; Tư vấn</span>
            </div>
            <h2 className="sp-section-title">Câu hỏi thường gặp</h2>
            <p className="sp-section-subtitle">
              Giải đáp nhanh chóng những băn khoăn phổ biến nhất trước khi bạn bắt đầu trải nghiệm SP Home.
            </p>
          </motion.div>

          <div className="sp-faq-list">
            {[
              {
                q: "SP Home có thu phí sử dụng của Chủ nhà không?",
                a: "SP Home cung cấp gói dùng thử miễn phí đầy đủ tính năng cơ bản cho chủ nhà quản lý quy mô vừa và nhỏ. Với các chuỗi trọ lớn cần phân quyền nâng cao và dịch vụ kết nối CTV VIP, chúng tôi có các gói cước siêu tiết kiệm chỉ bằng chi phí một tách cà phê mỗi tháng.",
              },
              {
                q: "Cộng tác viên (CTV) đăng ký có mất phí hay cần đặt cọc tiền không?",
                a: "Hoàn toàn KHÔNG. Bất kỳ ai cũng có thể đăng ký tài khoản CTV miễn phí 100%, được truy cập toàn bộ giỏ hàng phòng trống đã xác thực và nhận hoa hồng ngay khi chốt khách ký hợp đồng thành công.",
              },
              {
                q: "Hoa hồng của CTV được chi trả như thế nào và bao lâu nhận được?",
                a: "Sau khi khách thuê đặt cọc và ký hợp đồng thành công với chủ nhà, hoa hồng sẽ được đối soát tự động trên hệ thống và chuyển khoản trực tiếp vào tài khoản ngân hàng của bạn trong vòng 24h làm việc.",
              },
              {
                q: "Dữ liệu khách thuê và tài chính của tôi có được bảo mật không?",
                a: "Tuyệt đối an toàn. Dữ liệu của bạn được lưu trữ trên hạ tầng điện toán đám mây đạt tiêu chuẩn an ninh cao nhất. Chỉ chủ nhà và những người được chủ nhà cấp quyền mới có thể xem thông tin thu chi và danh sách khách thuê.",
              },
              {
                q: "Tôi quản lý nhiều nhà trọ ở các quận khác nhau thì có quản lý tập trung được không?",
                a: "Có, SP Home được thiết kế chuyên biệt cho mô hình chuỗi. Bạn có thể thêm không giới hạn chi nhánh/tòa nhà, phân quyền cho từng quản lý tòa riêng biệt trong khi tài khoản chủ nhà nắm bắt toàn bộ dòng tiền và tình trạng phòng theo thời gian thực.",
              },
            ].map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={idx}
                  className={`sp-faq-item ${isOpen ? "open" : ""}`}
                  onClick={() => setOpenFaq(isOpen ? null : idx)}
                >
                  <div className="sp-faq-question">
                    <span>{faq.q}</span>
                    <div className="sp-faq-toggle-icon">
                      {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </div>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        className="sp-faq-answer"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        <p>{faq.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ====================================================================
          INTEGRATED SYSTEM FOOTER
          ==================================================================== */}
      <Footer />

      {/* ====================================================================
          11. FLOATING CONTACT SPEED DIAL (HOTLINE, ZALO, MESSENGER)
          ==================================================================== */}
      <div className="sp-floating-contacts" aria-label="Kênh liên hệ nhanh">
        {/* 1. Phone / Hotline Button */}
        <div className="sp-float-btn-wrap">
          <div className="sp-float-tooltip">
            <span className="tooltip-tag">Hotline 24/7</span>
            <span className="tooltip-text">0911 742 286</span>
          </div>
          <a
            href="tel:0911742286"
            className="sp-float-btn phone"
            aria-label="Gọi hotline: 0911 742 286"
          >
            <span className="sp-float-pulse-ring" />
            <PhoneCall size={22} className="sp-float-phone-icon" />
          </a>
        </div>

        {/* 2. Zalo Button */}
        <div className="sp-float-btn-wrap">
          <div className="sp-float-tooltip">
            <span className="tooltip-tag">Zalo Official</span>
            <span className="tooltip-text">Quét mã &amp; Chat Zalo</span>
          </div>
          <button
            type="button"
            onClick={handleZaloButtonClick}
            className={`sp-float-btn zalo ${showZaloCard ? "active" : ""}`}
            aria-label="Tư vấn Zalo cùng SP Home"
          >
            <div className="sp-zalo-badge-icon">
              <svg viewBox="0 0 32 32" width="25" height="25" fill="none">
                <path
                  d="M16 3C8.82 3 3 8.37 3 15C3 18.5 4.58 21.65 7.15 23.85L5.7 28.3C5.53 28.82 6.05 29.28 6.55 29.05L12 26.55C13.28 26.85 14.62 27 16 27C23.18 27 29 21.63 29 15C29 8.37 23.18 3 16 3Z"
                  fill="#ffffff"
                />
                <text
                  x="16"
                  y="18.5"
                  textAnchor="middle"
                  fill="#0068ff"
                  fontSize="9.5"
                  fontWeight="900"
                  fontFamily="'Plus Jakarta Sans', -apple-system, sans-serif"
                  letterSpacing="-0.3px"
                >
                  Zalo
                </text>
              </svg>
            </div>
          </button>

          {/* Interactive Zalo Popover Card */}
          <AnimatePresence>
            {showZaloCard && (
              <motion.div
                className="sp-zalo-popover-card"
                initial={{ opacity: 0, scale: 0.88, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.88, y: 15 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Popover Header */}
                <div className="sp-zalo-popover-header">
                  <div className="sp-zalo-header-brand">
                    <div className="sp-zalo-mini-logo">Zalo</div>
                    <div className="sp-zalo-header-titles">
                      <h4 className="sp-zalo-popover-title">Kết nối Zalo tư vấn</h4>
                      <p className="sp-zalo-popover-sub">SP Home hỗ trợ 24/7</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="sp-zalo-popover-close"
                    onClick={() => setShowZaloCard(false)}
                    aria-label="Đóng bảng Zalo"
                  >
                    <X size={17} />
                  </button>
                </div>

                {/* Popover Body: QR Code */}
                <div className="sp-zalo-popover-body">
                  <div className="sp-zalo-qr-frame">
                    <QRCodeSVG
                      value="https://zalo.me/0911742286"
                      size={160}
                      level="H"
                      includeMargin={false}
                    />
                    <div className="sp-zalo-qr-center-badge">
                      <span>Zalo</span>
                    </div>
                  </div>

                  <p className="sp-zalo-qr-instruction">
                    Mở ứng dụng <strong>Zalo trên điện thoại</strong> và quét mã QR để bắt đầu nhắn tin ngay.
                  </p>

                  {/* Phone Copy Box */}
                  <div className="sp-zalo-phone-bar">
                    <div className="sp-zalo-phone-text">
                      <span className="label">Số Zalo:</span>
                      <span className="value">0911 742 286</span>
                    </div>
                    <button
                      type="button"
                      className={`sp-zalo-copy-btn ${copiedZalo ? "copied" : ""}`}
                      onClick={handleCopyZaloPhone}
                      title="Sao chép số điện thoại"
                    >
                      {copiedZalo ? (
                        <>
                          <Check size={13} />
                          <span>Đã chép</span>
                        </>
                      ) : (
                        <>
                          <Copy size={13} />
                          <span>Sao chép</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Action Buttons */}
                  <div className="sp-zalo-popover-actions">
                    <a
                      href="zalo://conversation?phone=0911742286"
                      className="sp-zalo-action-btn primary"
                      onClick={() => setShowZaloCard(false)}
                    >
                      Mở Zalo PC
                    </a>
                    <a
                      href="https://zalo.me/0911742286"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sp-zalo-action-btn secondary"
                      onClick={() => setShowZaloCard(false)}
                    >
                      <span>Zalo Web</span>
                      <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* 3. Facebook Messenger Button */}
        {/*
        <div className="sp-float-btn-wrap">
          <div className="sp-float-tooltip">
            <span className="tooltip-tag">Messenger</span>
            <span className="tooltip-text">Nhắn tin Facebook</span>
          </div>
          <a
            href="https://m.me/sphome"
            target="_blank"
            rel="noopener noreferrer"
            className="sp-float-btn messenger"
            aria-label="Nhắn tin qua Facebook Messenger"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="#ffffff">
              <path d="M12 2C6.477 2 2 6.145 2 11.258c0 2.91 1.455 5.514 3.737 7.218-.184 1.488-.724 3.321-1.789 4.673-.17.217-.118.528.113.684.144.098.33.109.484.03 2.977-1.528 5.094-2.88 5.922-3.46.495.08 1.008.123 1.533.123 5.523 0 10-4.145 10-9.268C22 6.145 17.523 2 12 2zm1.092 12.445l-2.613-2.791-5.1 2.791 5.61-5.955 2.68 2.79 5.033-2.79-5.61 5.955z"/>
            </svg>
          </a>
        </div>
        */}
      </div>

      {/* ====================================================================
          INTERACTIVE QUICK MODALS
          ==================================================================== */}
      <AnimatePresence>
        {activeModal && (
          <div className="sp-modal-overlay" onClick={() => setActiveModal(null)}>
            <motion.div
              className="sp-modal-card"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="sp-modal-close-btn"
                onClick={() => setActiveModal(null)}
              >
                <FaTimes />
              </button>

              {activeModal === "landlord" && (
                <>
                  <div className="sp-modal-icon green">
                    <FaHome />
                  </div>
                  <h3 className="sp-modal-title">Quản lý nhà trọ cùng SP Home</h3>
                  <p className="sp-modal-desc">
                    Hệ thống thông minh giúp bạn quản lý phòng, theo dõi hợp đồng, điện nước và thu tiền tự động.
                  </p>
                  <div className="sp-modal-actions">
                    <button
                      className="sp-modal-btn primary"
                      onClick={() => navigate("/admin/dashboard")}
                    >
                      Đến trang Quản lý
                    </button>
                    <button
                      className="sp-modal-btn secondary"
                      onClick={() => navigate("/login")}
                    >
                      Đăng nhập
                    </button>
                  </div>
                </>
              )}

              {activeModal === "ctv" && (
                <>
                  <div className="sp-modal-icon blue">
                    <FaUsers />
                  </div>
                  <h3 className="sp-modal-title">Tham gia mạng lưới CTV</h3>
                  <p className="sp-modal-desc">
                    Đăng tin phòng trống, tìm khách và nhận hoa hồng hấp dẫn, minh bạch từ SP Home ngay hôm nay!
                  </p>
                  <div className="sp-modal-actions">
                    <button
                      className="sp-modal-btn primary"
                      style={{ background: "#1d72b8" }}
                      onClick={() => navigate("/rooms")}
                    >
                      Xem danh sách phòng
                    </button>
                    <button
                      className="sp-modal-btn secondary"
                      onClick={() => navigate("/login")}
                    >
                      Đăng nhập
                    </button>
                  </div>
                </>
              )}

              {activeModal === "start" && (
                <>
                  <div className="sp-modal-icon green">
                    <FaHome />
                  </div>
                  <h3 className="sp-modal-title">Chào mừng bạn đến với SP Home!</h3>
                  <p className="sp-modal-desc">
                    Chọn vai trò để bắt đầu trải nghiệm giải pháp toàn diện cho nhà trọ:
                  </p>
                  <div className="sp-modal-actions">
                    <button
                      className="sp-modal-btn primary"
                      onClick={() => navigate("/admin/dashboard")}
                    >
                      Tôi là Chủ nhà
                    </button>
                    <button
                      className="sp-modal-btn primary"
                      style={{ background: "#1d72b8" }}
                      onClick={() => navigate("/rooms")}
                    >
                      Tôi là CTV
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
